/**
 * useChatSessions — multi-session management (web port of RN's hook).
 *
 * Lists the user's chat sessions (one per guide/destination), creates new ones
 * bound to an agent, and selects the active one (persisted per-env in
 * localStorage). Powers the agent switcher. Session metadata (avatar + display
 * name) is cached so the UI survives a transient 401/empty response.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import {
  useChatSession,
  type ChatSession,
} from '../context/ChatSessionContext';
import { auth } from '../client';
import httpClient from '../http';
import { useStorage } from './useStorage';
import config from '../../config/env.json';

const CHAT_API = '/functions/v1/chat';
const ENV = (config as { ENV?: string }).ENV || 'dev';
const SELECTED_KEY = `selected-session_${ENV}`;
const avatarKey = (sid: string) => `agent-avatar_${ENV}_${sid}`;
const nameKey = (sid: string) => `agent-name_${ENV}_${sid}`;

interface SessionRow {
  id: string;
  title?: string;
  agent_id?: string;
  agent_display_name?: string;
  agent_avatar_url?: string;
  last_message_at?: string;
  location?: string;
  greetings?: {
    morning?: string;
    afternoon?: string;
    evening?: string;
    night?: string;
  };
}

export function useChatSessions() {
  const {
    sessions,
    setSessions,
    selectedSessionId,
    setSelectedSessionId,
    setAgent,
  } = useChatSession();
  const { getItem, setItem, removeItem } = useStorage();
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const hydratedRef = useRef(false);

  const getUserId = useCallback(async (): Promise<string | undefined> => {
    try {
      const info = await auth.getSessionInfo();
      return info?.userId || undefined;
    } catch {
      return undefined;
    }
  }, []);

  const listSessions = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const res = await httpClient
        .get<{ sessions?: SessionRow[] } | SessionRow[]>(`${CHAT_API}/sessions`)
        .catch(() => ({ data: { sessions: [] }, status: 0 }) as never);
      const status: number = (res as { status?: number }).status ?? 0;
      const isSuccess = status >= 200 && status < 300;
      const rows: SessionRow[] = Array.isArray(res.data)
        ? res.data
        : (res.data?.sessions ?? []);
      const mapped: ChatSession[] = rows.map(s => {
        let avatarUrl = s.agent_avatar_url;
        let displayName = s.agent_display_name;
        if (isSuccess && avatarUrl) setItem(avatarKey(s.id), avatarUrl);
        else if (!avatarUrl) avatarUrl = getItem(avatarKey(s.id)) || undefined;
        if (isSuccess && displayName) setItem(nameKey(s.id), displayName);
        else if (!displayName)
          displayName = getItem(nameKey(s.id)) || undefined;
        return {
          id: s.id,
          title: s.title,
          agentId: s.agent_id,
          agentDisplayName: displayName,
          agentAvatarUrl: avatarUrl,
          last_message_at: s.last_message_at,
          location: s.location,
          greetings: s.greetings,
        };
      });
      setSessions(prev =>
        prev.length === mapped.length &&
        prev.every((p, i) => p.id === mapped[i]?.id)
          ? prev
          : mapped,
      );
    } catch (err) {
      setError(
        err instanceof Error ? err : new Error('Failed to fetch sessions'),
      );
    } finally {
      setIsLoading(false);
    }
  }, [getItem, setItem, setSessions]);

  const selectSession = useCallback(
    async (sessionId: string, override?: ChatSession) => {
      try {
        const session = override ?? sessions.find(s => s.id === sessionId);
        const userId = await getUserId();
        setItem(
          SELECTED_KEY,
          JSON.stringify({
            sessionId,
            title: session?.title || 'Unknown Session',
            agentId: session?.agentId || 'unknown',
            userId,
          }),
        );
        setSelectedSessionId(sessionId);
        if (session) {
          setAgent({
            id: session.agentId,
            displayName: session.agentDisplayName,
            avatarUrl: session.agentAvatarUrl,
            greetings: session.greetings,
          });
        }
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Failed to select session'),
        );
      }
    },
    [sessions, getUserId, setItem, setSelectedSessionId, setAgent],
  );

  const createSession = useCallback(
    async (
      title: string,
      agentId: string,
      select = false,
      destinationLocation?: ChatSession['destinationLocation'],
    ): Promise<ChatSession> => {
      setIsLoading(true);
      setError(null);
      try {
        const { data } = await httpClient.post<
          { session?: SessionRow } & SessionRow
        >(`${CHAT_API}/sessions`, { title, agentId });
        const s = (data?.session || data) as SessionRow;
        if (!s?.id) throw new Error('Invalid session response from server');
        if (s.agent_avatar_url) setItem(avatarKey(s.id), s.agent_avatar_url);
        if (s.agent_display_name) setItem(nameKey(s.id), s.agent_display_name);
        const newSession: ChatSession = {
          id: s.id,
          title: s.title,
          agentId: s.agent_id,
          agentDisplayName: s.agent_display_name,
          agentAvatarUrl: s.agent_avatar_url,
          last_message_at: s.last_message_at,
          destinationLocation,
        };
        setSessions(prev =>
          prev.some(p => p.id === newSession.id) ? prev : [...prev, newSession],
        );
        if (select) await selectSession(newSession.id, newSession);
        return newSession;
      } catch (err) {
        setError(
          err instanceof Error ? err : new Error('Failed to create session'),
        );
        throw err;
      } finally {
        setIsLoading(false);
      }
    },
    [setItem, setSessions, selectSession],
  );

  const clearSessions = useCallback(async () => {
    setSelectedSessionId(null);
    setSessions([]);
  }, [setSelectedSessionId, setSessions]);

  // Hydrate the selected session from storage + load the list on mount, and
  // re-run on auth changes. User-change reset is handled in ChatSessionContext.
  useEffect(() => {
    const hydrate = async () => {
      try {
        const stored = getItem(SELECTED_KEY);
        if (!stored) return;
        const { sessionId, userId: storedUserId } = JSON.parse(stored);
        const currentUserId = await getUserId();
        if (storedUserId && currentUserId && storedUserId !== currentUserId) {
          removeItem(SELECTED_KEY);
          return;
        }
        if (sessionId) setSelectedSessionId(sessionId);
      } catch {
        /* noop */
      }
    };
    if (!hydratedRef.current) {
      hydratedRef.current = true;
      hydrate();
      listSessions();
    }
    const unsub = auth.onAuthEvent(async event => {
      if (event === 'sessionUpdated') {
        await hydrate();
        await listSessions();
      } else if (event === 'sessionCleared') {
        clearSessions();
      }
    });
    return () => unsub();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return {
    sessions,
    selectedSessionId,
    setSelectedSessionId,
    isLoading,
    error,
    listSessions,
    createSession,
    selectSession,
    clearSessions,
  };
}
